/**
 * Cible du lien d'invitation partageable d'un rendez-vous (bouton "Partager le
 * lien" côté tuteur, ou notification push `rendezvous_scheduled`). Accessible
 * tuteur OU élève, l'un ou l'autre participant (RLS own_select/tutor_select
 * sur tutor_booking_reservations) — l'orientation d'affichage ("avec X") est
 * déduite du profil connecté.
 */

import { useParams } from '@tanstack/react-router';
import { CalendarClock, Video } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { RENDEZ_VOUS_KIND_LABELS } from '@jeraisonne/db';
import { isRendezVousUpcoming, isVisioRoomJoinable, toEndedAtMs } from '@jeraisonne/db/visio';
import { useAuth } from '@/lib/auth';
import { useNow } from '@/hooks/use-now';
import { useJoinVisioRoom } from '@/lib/visio/handoff';
import {
  useRendezVousDetail,
  useRendezVousDialogue,
  useSetRendezVousMessageHidden,
} from '@/lib/tutor/rendezvous';
import { RendezVousBackButton } from '@/components/rendezvous-back-button';
import { RendezVousDialogue } from '@/components/rendezvous-dialogue';
import { RendezVousFiles } from '@/components/rendezvous-files';
import { RendezVousSummaryPanel } from '@/components/rendezvous-summary-panel';

function formatSlotDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function RendezVousJoinPage() {
  const { reservationId } = useParams({ strict: false }) as { reservationId: string };
  const { user } = useAuth();
  const { data: rdv, isLoading } = useRendezVousDetail(reservationId);
  const now = useNow(30_000);
  const joinVisioRoom = useJoinVisioRoom();
  // Rendez-vous PASSÉ = sa salle n'est plus rejoignable (même critère que les
  // listes « à venir ») : on y lit le dialogue du chat, et le tuteur y génère
  // son résumé. Calculé avant les retours anticipés (règle des hooks).
  const isPast =
    !!rdv && !isRendezVousUpcoming(now, new Date(rdv.endsAt).getTime(), toEndedAtMs(rdv.endedAt));
  const { data: dialogue, isLoading: dialogueLoading } = useRendezVousDialogue(
    reservationId,
    isPast,
  );
  const setHidden = useSetRendezVousMessageHidden(reservationId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!rdv || rdv.status !== 'confirmed') {
    return (
      <div className="mx-auto max-w-sm px-4 py-6 sm:py-10">
        <RendezVousBackButton />
        <div className="py-10 text-center">
          <CalendarClock className="text-ink-300 mx-auto h-8 w-8" />
          <p className="text-ink-700 mt-2 font-sans text-sm font-semibold">
            Rendez-vous introuvable
          </p>
          <p className="text-ink-500 mt-1 font-sans text-xs">
            Ce lien n'est plus valide, ou le rendez-vous a été annulé.
          </p>
        </div>
      </div>
    );
  }

  const isTutor = user?.id === rdv.tutorId;
  const otherName = isTutor ? (rdv.studentName ?? 'Élève') : rdv.tutorName;
  const joinable = isVisioRoomJoinable(
    now,
    new Date(rdv.startsAt).getTime(),
    new Date(rdv.endsAt).getTime(),
    toEndedAtMs(rdv.endedAt),
  );

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 text-center sm:py-10">
      <RendezVousBackButton />
      <div className="mx-auto max-w-sm">
        <div className="bg-primary-50 mx-auto flex h-14 w-14 items-center justify-center rounded-full">
          <Video className="text-primary-600 h-6 w-6" />
        </div>
        <p className="bg-ink-100 text-ink-600 mx-auto mt-4 inline-block rounded-full px-2.5 py-1 text-xs font-semibold">
          {RENDEZ_VOUS_KIND_LABELS[rdv.kind]}
        </p>
        <h1 className="font-display text-ink-900 mt-2 text-xl font-extrabold tracking-tight">
          Rendez-vous avec {otherName}
        </h1>
        <p className="text-ink-500 mt-1 font-sans text-sm first-letter:uppercase">
          {formatSlotDate(rdv.startsAt)}
        </p>

        {joinable ? (
          <Button
            onClick={() => joinVisioRoom.mutate(rdv.reservationId)}
            loading={joinVisioRoom.isPending}
            className="mt-6 min-h-[2.75rem] w-full active:scale-[0.98]"
          >
            <Video className="size-4" />
            Rejoindre la salle
          </Button>
        ) : isPast ? (
          <p className="text-ink-500 mt-6 font-sans text-xs">Ce rendez-vous est terminé.</p>
        ) : (
          <p className="text-ink-500 mt-6 font-sans text-xs">
            Le bouton pour rejoindre apparaîtra 10 minutes avant le début.
          </p>
        )}
      </div>

      {isPast && (
        <div className="mt-6 space-y-4">
          <RendezVousDialogue
            messages={dialogue?.messages ?? []}
            people={dialogue?.people ?? {}}
            isLoading={dialogueLoading}
            myUserId={user?.id}
            otherName={otherName}
            canModerate={isTutor}
            onSetHidden={(messageId, hidden) => setHidden.mutate({ messageId, hidden })}
            pending={setHidden.isPending}
          />
          <RendezVousFiles reservationId={rdv.reservationId} isHost={isTutor} />
          {isTutor && (
            <RendezVousSummaryPanel
              reservationId={rdv.reservationId}
              // Les messages masqués n'entrent pas dans le résumé : on ne compte que les visibles.
              messageCount={(dialogue?.messages ?? []).filter((m) => !m.hiddenAt).length}
            />
          )}
        </div>
      )}
    </div>
  );
}
